import type { LucideIcon } from 'lucide-react'
import {
  Baby,
  Building2,
  ChevronDown,
  Coffee,
  Cross,
  DoorOpen,
  Dumbbell,
  Euro,
  GraduationCap,
  MapPin,
  Maximize2,
  Music,
  Star,
  Store,
  Train,
  Trees,
  Truck,
  UtensilsCrossed,
  X
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  apartmentAnalyticsProps,
  trackEvent
} from '@services/livability/analytics'
import {
  getApartment,
  getPriceHistory
} from '@services/livability/apartments'
import { getApartmentGalleryUrls } from '@services/livability/images'
import { getNearbyPlaces } from '@services/livability/places'
import type {
  Apartment,
  IndexCategory,
  NearbyPlacesResponse,
  Place,
  PriceHistory
} from '@services/livability/types'

import { Button } from '@/components/ui/button'
import { cn, formatPrice } from '@/lib/utils'

import { useLikeApartment } from '../utils/useLikeApartment'
import { ApartmentGallery } from './ApartmentGallery'
import { ApartmentIndexDisplay } from './ApartmentIndexDisplay'
import { ApartmentPriceChart } from './ApartmentPriceChart'

interface ApartmentDetailsPanelProps {
  apartment: Apartment
  onClose: () => void
  detailPath: string
  indexCategory?: IndexCategory
}

const PLACE_ICONS: Record<string, LucideIcon> = {
  schools: GraduationCap,
  grocery_stores: Store,
  parks: Trees,
  public_transport: Train,
  couriers: Truck,
  kindergartens: Baby,
  universities: Building2,
  gyms: Dumbbell,
  night_clubs: Music,
  restaurants: UtensilsCrossed,
  pharmacies: Cross,
  cafes: Coffee
}

type PlaceCategory = keyof NearbyPlacesResponse & keyof typeof PLACE_ICONS

const PLACE_CATEGORIES: PlaceCategory[] = [
  'schools',
  'kindergartens',
  'universities',
  'grocery_stores',
  'pharmacies',
  'cafes',
  'restaurants',
  'parks',
  'gyms',
  'public_transport',
  'night_clubs',
  'couriers'
]

export function ApartmentDetailsPanel({
  apartment: initialApartment,
  onClose,
  detailPath,
  indexCategory = 'general'
}: ApartmentDetailsPanelProps) {
  const { t } = useTranslation('livability')
  const navigate = useNavigate()
  const [isNearbyExpanded, setIsNearbyExpanded] = useState(true)
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlacesResponse | null>(
    null
  )
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false)
  const [apartment, setApartment] = useState(initialApartment)
  const [isLoadingIndex, setIsLoadingIndex] = useState(false)
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([])
  const [isLoadingPriceHistory, setIsLoadingPriceHistory] = useState(false)
  const handleLikeApartment = useLikeApartment(apartment, indexCategory, 'map')

  useEffect(() => {
    if (indexCategory) {
      setIsLoadingIndex(true)
      getApartment(initialApartment.id, true, indexCategory)
        .then(setApartment)
        .catch(() => {})
        .finally(() => setIsLoadingIndex(false))
    }
  }, [initialApartment.id, indexCategory])

  useEffect(() => {
    setIsLoadingPlaces(true)
    setNearbyPlaces(null)
    getNearbyPlaces(initialApartment.id, 500)
      .then(setNearbyPlaces)
      .catch(() => {})
      .finally(() => setIsLoadingPlaces(false))
  }, [initialApartment.id])

  useEffect(() => {
    setIsLoadingPriceHistory(true)
    getPriceHistory(initialApartment.id)
      .then(setPriceHistory)
      .catch(() => {})
      .finally(() => setIsLoadingPriceHistory(false))
  }, [initialApartment.id])

  const galleryImages = getApartmentGalleryUrls(apartment)

  const totalPlaces = nearbyPlaces
    ? PLACE_CATEGORIES.reduce((sum, category) => {
        const places = nearbyPlaces[category]
        return sum + (Array.isArray(places) ? places.length : 0)
      }, 0)
    : 0

  return (
    <div className="absolute top-0 right-0 z-20 flex h-full w-full max-w-96 animate-in slide-in-from-right flex-col border-l border-ink/[0.06] bg-background shadow-xl duration-300">
      <div className="flex items-center justify-between border-b border-ink/[0.06] p-4">
        <h2 className="text-lg font-semibold">{t('map.detailTitle')}</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={t('detail.back')}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <ApartmentGallery images={galleryImages} title={apartment.title} compact />

        <div>
          <h3 className="mb-2 text-xl font-bold">{apartment.title}</h3>
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              {apartment.street_address && (
                <div className="font-medium text-foreground">
                  {apartment.street_address}
                </div>
              )}
              <span>{apartment.location}</span>
              {apartment.geocode_precision === 'APPROXIMATE' && (
                <div className="mt-0.5 text-xs italic">
                  {t('detail.approximateLocation')}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-primary/10 p-4">
          <div className="flex items-center gap-2">
            <Euro className="h-5 w-5 text-primary" />
            <div>
              <div className="text-2xl font-bold text-primary">
                {apartment.price_month > 0
                  ? apartment.price_month.toLocaleString()
                  : formatPrice(apartment.price_month)}
              </div>
              {apartment.price_month > 0 && (
                <div className="text-sm text-muted-foreground">
                  {t('map.perMonth')}
                </div>
              )}
            </div>
          </div>
        </div>

        {isLoadingIndex ? (
          <div className="flex items-center justify-center py-4">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : apartment.index ? (
          <ApartmentIndexDisplay index={apartment.index} compact={true} />
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl bg-ink/[0.03] p-3">
            <div className="mb-1 flex items-center gap-2">
              <Maximize2 className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">
                {t('map.area')}
              </span>
            </div>
            <div className="text-lg font-semibold">{apartment.area_m2} m²</div>
          </div>

          <div className="rounded-2xl bg-ink/[0.03] p-3">
            <div className="mb-1 flex items-center gap-2">
              <DoorOpen className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">
                {t('map.rooms')}
              </span>
            </div>
            <div className="text-lg font-semibold">{apartment.room_count}</div>
          </div>
        </div>

        {isLoadingPriceHistory ? (
          <div className="flex items-center justify-center py-4">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : priceHistory.length > 0 ? (
          <ApartmentPriceChart priceHistory={priceHistory} />
        ) : null}

        <div className="space-y-3 border-t border-ink/[0.06] pt-4">
          <button
            type="button"
            onClick={() => setIsNearbyExpanded((v) => !v)}
            aria-expanded={isNearbyExpanded}
            className="flex w-full appearance-none items-center gap-2.5 border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:outline-none"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <MapPin className="h-4 w-4" />
            </span>
            <h4 className="font-display text-base font-bold tracking-tight text-ink">
              {t('map.nearbyPlaces')}
            </h4>
            <span className="ml-auto flex items-center gap-2">
              {totalPlaces > 0 && (
                <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 text-[11px] font-medium text-ink/60">
                  {totalPlaces}
                </span>
              )}
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform duration-200',
                  isNearbyExpanded && 'rotate-180'
                )}
              />
            </span>
          </button>

          <AnimatePresence initial={false}>
            {isNearbyExpanded && (
              <motion.div
                key="nearby"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="space-y-4 pt-1">
                  {isLoadingPlaces ? (
                <div className="py-4 text-center">
                  <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('map.loadingNearby')}
                  </p>
                </div>
              ) : nearbyPlaces ? (
                PLACE_CATEGORIES.map((category) => {
                  const places = nearbyPlaces[category] as Place[]
                  if (!Array.isArray(places) || places.length === 0) return null

                  const Icon = PLACE_ICONS[category] || MapPin
                  const displayCount = Math.min(places.length, 3)

                  return (
                    <div key={category} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span className="text-xs font-semibold text-ink">
                          {t(`detail.places.${category}`)}
                        </span>
                        <span className="rounded-full bg-ink/[0.05] px-1.5 py-0.5 text-[11px] font-medium text-ink/60">
                          {places.length}
                        </span>
                      </div>
                      <div className="space-y-0.5">
                        {(places as Place[])
                          .slice(0, displayCount)
                          .map((place, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-ink/[0.03]"
                            >
                              <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm text-ink">
                                  {place.name}
                                </span>
                                {place.rating != null && (
                                  <span className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-ink/55">
                                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                    {place.rating.toFixed(1)}
                                  </span>
                                )}
                              </div>
                              {place.distance_meters != null && (
                                <span className="shrink-0 text-xs font-semibold text-ink">
                                  {place.distance_meters} m
                                </span>
                              )}
                            </div>
                          ))}
                        {places.length > displayCount && (
                          <div className="px-2 pt-0.5 text-xs text-muted-foreground">
                            {t('map.moreCount', {
                              count: places.length - displayCount
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  {t('map.noNearby')}
                </p>
              )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2 overflow-hidden border-t border-ink/[0.06] bg-background p-4">
        <Button
          className="min-w-0 self-stretch hover:scale-100 hover:translate-y-0 hover:shadow-none"
          variant="outline"
          onClick={handleLikeApartment}
        >
          {t('detail.like')}
        </Button>
        <Button
          className="min-w-0 self-stretch hover:scale-100 hover:translate-y-0 hover:shadow-none"
          onClick={() => {
            trackEvent(
              'apartment_clicked',
              apartmentAnalyticsProps(apartment, 'map', indexCategory)
            )
            navigate(detailPath)
          }}
        >
          {t('map.viewFullDetail')}
        </Button>
      </div>
    </div>
  )
}
